import { faCalendarClock, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Sticky } from 'src/components/Sticky';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import {
  isAppointmentCancelLate,
  isAppointmentFuture,
  isRescheduleDisabledByLateCancellationPolicy,
} from 'src/models/appointment/utils';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { formatCurrency } from 'src/utils/numberFormatter';

import {
  ActionTextProps,
  useAppointmentDrawerActions,
} from './useAppointmentDrawerActions';

import type { Appointment } from 'src/models/appointment/_types';
import type { AccountManager } from 'src/models/user/_types';

interface Props {
  appointment: Appointment;
  dependents?: AccountManager[];
}

export const AppointmentDrawerActions: React.FC<Props> = ({
  appointment,
  dependents = [],
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentDrawerActions',
  });

  const {
    visit,
    entry,
    textProps,
    buttonProps,
    showConfirm,
    setShowConfirm,
    handleCancelConfirm,
    handleConfirm,
    showUnCancel,
    setShowCancelOptions,
    showCancelOptions,
    showPersonSelector,
    personOptions,
    selectedPerson,
    setSelectedUserId,
    dialogTitle,
  } = useAppointmentDrawerActions({
    appointment,
    dependents,
  });

  const { session } = useSession();

  // not a trainer, and not a member, or appointment has already started
  if (
    ((!visit && !entry) || !isAppointmentFuture(appointment)) &&
    !showUnCancel
  ) {
    return null;
  }

  return (
    <PermissionCheck
      can={showUnCancel ? 'editAppointmentMembers' : 'checkInAppointment'}
      object={appointment}
    >
      {/* Person Selector - only show if there are multiple people and button actions */}
      {showPersonSelector && buttonProps.length > 0 && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Autocomplete
            options={personOptions}
            value={selectedPerson}
            onChange={(_, value) => {
              if (value?.id) {
                setSelectedUserId(value.id);
              }
            }}
            getOptionLabel={(option) => option?.title || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select person"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </Box>
      )}
      <Dialog
        open={showCancelOptions}
        title={dialogTitle}
        onClose={() => setShowCancelOptions(false)}
      >
        {!session?.disableRescheduling &&
          !isRescheduleDisabledByLateCancellationPolicy({
            appointment,
            session,
          }) && (
            <ListItem
              primary={t('reschedule')}
              clickable={true}
              icon={faCalendarClock}
              onClick={() => {
                setShowCancelOptions(true);
                const returnUrl = encodeURIComponent(window.location.pathname);
                window.location.href = externalLinks.v3Url(
                  `/booking/?rescheduleId=${visit.id}&userId=${selectedPerson?.id}&returnUrl=${returnUrl}`
                );
              }}
            />
          )}
        <ListItem
          primary={t('cancelSession')}
          clickable={true}
          icon={faTimesCircle}
          onClick={() => {
            setShowCancelOptions(true);
            setShowConfirm(true);
          }}
        />
      </Dialog>
      <AppointmentConfirmDialog
        appointment={appointment}
        open={showConfirm}
        type={entry ? 'waitlist' : 'session'}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
      />
      <Sticky location="bottom">
        {/* Text Section */}
        <ActionText {...textProps} />
        {/* Action Section */}
        {buttonProps.length ? (
          <Stack spacing={1} sx={{ p: 1 }} direction="row" alignItems="center">
            {buttonProps.map(({ label, ...otherProps }, i) => (
              <Button fullWidth size="small" key={i} {...otherProps}>
                {label}
              </Button>
            ))}
          </Stack>
        ) : null}
      </Sticky>
    </PermissionCheck>
  );
};

const ActionText: React.FC<ActionTextProps> = ({
  bgColor = 'primary.lightest',
  color = 'primary.darkest',
  text,
}) => {
  return (
    <Box bgcolor={bgColor} textAlign="center" py={1}>
      <Typography variant="body2" color={color}>
        {text}
      </Typography>
    </Box>
  );
};

const AppointmentConfirmDialog = ({
  appointment,
  open,
  onClose,
  onConfirm,
  type = 'session',
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentDrawerActions',
  });
  const { session } = useSession();
  const lateCancel = isAppointmentCancelLate({ appointment, session });
  const { autoChargeCancellation, cancellationFee } = appointment.service;
  const hasFee = autoChargeCancellation && cancellationFee > 0;

  return (
    <ConfirmDialog
      open={open}
      title={
        type == 'session' ? t('cancelAppointment') : t('removeFromWaitlist')
      }
      actions={[
        {
          label:
            type == 'session'
              ? t('cancelAppointment')
              : t('removeFromWaitlist'),
          color: 'error',
          variant: 'contained',
          onClick: () => onConfirm(),
        },
        {
          label: t('goBack'),
          onClick: () => onClose(),
          color: 'secondary',
          variant: 'outlined',
        },
      ]}
    >
      {type == 'session' && (
        <>
          {t('cancelAppointmentConfirm')}
          {lateCancel && hasFee && (
            <>
              {' '}
              {t('cancellationFee', {
                fee: formatCurrency(cancellationFee, {
                  currency: session?.defaultCurrency,
                }),
              })}
            </>
          )}
          {lateCancel && !hasFee && <> {t('visitDeduction')}</>}
          {!lateCancel && <> {t('cannotBeUndone')}</>}
        </>
      )}
      {type != 'session' && (
        <>
          <Typography>{t('waitlistWarning')}</Typography>
        </>
      )}
    </ConfirmDialog>
  );
};
